import { useEffect } from "react";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../../pages/Home/HomeContent";
import {
  WelcomeContentEnum,
  WelcompeStepEnum,
} from "../../../../../types/welcome";
import ResonanceLoader from "../../../../atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../../../molecules/DisplayContent";
import NextPrevButtons from "../../../../molecules/NextPrevButtons";
import useOnBoardingApi from "./useOnBoardingApi";
import Container from "../../../../atoms/Container";
import { useResonanceLayout } from "../../../ResonanceContext/ResonanceLayoutContext";
import { usePageBackground } from "../../../../../hooks/usePageBackground";
import VideoOnBoarding from "./VideoOnboarding";
import SwiperOnboarding from "./SwiperOnboarding";
import { getProjectConfig } from "../../../../../projects";
import { resolveOnboarding } from "../../../../../helper/onboarding";



const OnBoarding = (): ReactElement => {
  const {
    i18n: { language },
  } = useTranslation();

  const { welcomeData, setCurrentStep, goPrevious } = useHomeContext();
  const { selectedRole, app } = useConfig();

  // null when the researcher hasn't created an onboarding page for this study.
  const contentId =
    welcomeData.contents[WelcomeContentEnum.ONBOARDING_STUDY]?.id ?? null;
  const studyId = welcomeData.id;

  // Optional developer-provided onboarding UI (real code instead of EditorJS).
  const CustomOnboarding = getProjectConfig().onboardingComponent;

  // What this study's onboarding shows. The onboarding page's EXISTENCE is the
  // master switch (no page -> 'none' -> the step is skipped); its type comes from
  // the ONBOARDING study_config (app.onboarding, set on that page in Diapason). A
  // project custom component is the code escape hatch.
  const { mode, videoIds, willRender } = resolveOnboarding({
    studyOnboarding: app?.onboarding ?? null,
    hasContent: contentId != null,
    hasCustomComponent: !!CustomOnboarding,
    projectStyle: getProjectConfig().onboardingStyle,
  });

  const goToNextStep = () => {
    const has_inclusion =
      selectedRole?.inclusion_criteria?.has_inclusion_questions;
    setCurrentStep(
      has_inclusion ? WelcompeStepEnum.INCLUSION : WelcompeStepEnum.INSCRIPTION
    );
  };

  const { content, loadContent } = useOnBoardingApi(
    studyId,
    contentId,
    language
  );

  const { setResonanceLayout } = useResonanceLayout();

   useEffect(() => {
    setResonanceLayout({
      // Video takes the full height, so the page header is hidden for it;
      // swiper/page/custom onboarding keep the header (a custom component can
      // hide it itself via useResonanceLayout).
      disableHeader: !(
        mode === "swiper" ||
        mode === "page" ||
        mode === "custom"
      ),
      disableNav: true,
      backgroundColor: "white",
      title: "onboarding_title",
      welcomePage: false,
    })

    return () =>
      setResonanceLayout({
        disableHeader: false,
      });
  }, [mode]);

  useEffect(() => {
    loadContent();
  }, []);

  // Authored page background (page + swiper modes); wins over the "white" set above
  // because it applies when the content arrives, after the mode effect.
  usePageBackground(content?.page_style);

  // Nothing to show for this study ('none'): skip straight past the onboarding
  // step. HomeContent already avoids entering the step in this case, so this is
  // a defensive guard against ever rendering an empty onboarding page.
  useEffect(() => {
    if (!willRender) goToNextStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nothing to render (mode 'none', or a mode whose content/videos are missing):
  // show nothing while the skip effect above advances to the next step.
  if (!willRender) {
    return <ResonanceLoader />;
  }

  // Developer-provided onboarding (real code) — the project opted out of EditorJS.
  if (mode === "custom" && CustomOnboarding) {
    return (
      <CustomOnboarding onFinish={goToNextStep} onPrevious={goPrevious} />
    );
  }

  // Block on the loader only while the authored content (swiper/page) is still
  // being fetched; video/custom don't use it.
  const needsContent = mode === "swiper" || mode === "page";
  if (needsContent && typeof contentId === "number" && !content) {
    return <ResonanceLoader />;
  }

  // Researcher-authored video onboarding (Cloudflare Stream ids from the config).
  if (mode === "video" && videoIds?.length) {
    return (
      <Container>
        <VideoOnBoarding videoIds={videoIds} />
        <NextPrevButtons
          onNextClick={goToNextStep}
          onPreviousClick={goPrevious}
        />
      </Container>
    );
  }

  // Researcher-authored onboarding page (server Content), when one exists.
  if ((mode === "swiper" || mode === "page") && content) {
    // Classic multi-screen swiper: same authored content, split into slides on
    // delimiter blocks.
    if (mode === "swiper") {
      return (
        <SwiperOnboarding
          content={content.content}
          onFinish={goToNextStep}
          onPrevious={goPrevious}
        />
      );
    }

    return (
      <DisplayContent
        editorJsData={content.content}
        buttonComp={
          <NextPrevButtons
            onNextClick={goToNextStep}
            onPreviousClick={goPrevious}
          />
        }
      />
    );
  }

  // willRender was true but no branch matched (e.g. a mode whose content is
  // mid-load): keep the loader rather than ever flashing an empty onboarding.
  return <ResonanceLoader />;
};

export default OnBoarding;
